import React from 'react'
import { Column } from 'react-table'
import { WorkPeriodEvent } from '../../api/work-period-events'
import { EmployerRule } from '../../api/employer-rules'
import Table from '../table'
import { RequestButton } from '../request-components/request-components'
import { MutationState } from '../../queries'
import locales from '../../../../locales/work-period-events'
import { getDocIdAsString } from '../../utils/documents'
import { buildEventRulesMap } from '../../utils/employer-rules'

interface WorkPeriodEventsListProps {
  workPeriodEvents: WorkPeriodEvent[]
  employerRules?: EmployerRule[]
  onEventClick?: (workPeriodEvent: WorkPeriodEvent) => void
  mutation?: MutationState
}

/**
 * Displays a table of work period events with their code, title and type.
 * When employerRules is provided, an extra column shows which rules reference each event.
 */
const WorkPeriodEventsList: React.FC<WorkPeriodEventsListProps> = ({
  workPeriodEvents,
  employerRules,
  onEventClick,
  mutation,
}) => {
  const eventRulesMap = React.useMemo(
    () => (employerRules ? buildEventRulesMap(employerRules) : undefined),
    [employerRules],
  )

  const tableColumns = useWorkPeriodEventsColumns(
    workPeriodEvents,
    onEventClick,
    mutation,
    eventRulesMap,
  )

  return (
    <Table<WorkPeriodEvent>
      columns={tableColumns}
      data={workPeriodEvents}
      noDataMessage="Aucune donnée"
    />
  )
}

const useWorkPeriodEventsColumns = (
  workPeriodEvents: WorkPeriodEvent[],
  onClick?: (workPeriodEvent: WorkPeriodEvent) => void,
  mutation?: MutationState,
  eventRulesMap?: Map<string, string[]>,
): Column<WorkPeriodEvent>[] => {
  return React.useMemo(() => {
    const columns: Column<WorkPeriodEvent>[] = [
      {
        Header: 'Code',
        accessor: 'code',
      },
      {
        Header: 'Intitulé',
        accessor: 'title',
        Cell: (data: any) => {
          const workPeriodEvent: WorkPeriodEvent = data.cell.row.original
          return (
            <RequestButton
              color="ghost"
              onClick={() => onClick?.(workPeriodEvent)}
              date-test={`edit-work-period-event-${getDocIdAsString(workPeriodEvent)}`}
              size={'small'}
              disabled={!onClick}
              //@ts-ignore
              mutation={mutation?.data?._id === workPeriodEvent._id ? mutation : undefined}
            >
              {workPeriodEvent.title}
            </RequestButton>
          )
        },
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: (data: any) => {
          const workPeriodEvent: WorkPeriodEvent = data.cell.row.original
          return locales.types[workPeriodEvent.type]
        },
      },
      {
        Header: 'Indique absence',
        accessor: 'indicatesAbsence',
        Cell: (data: any) => (data.value ? 'Oui' : 'Non'),
      },
    ]

    if (eventRulesMap) {
      columns.push({
        Header: 'Règles employeur',
        id: 'employerRules',
        Cell: (data: any) => {
          const event: WorkPeriodEvent = data.cell.row.original
          const ruleNames = eventRulesMap.get(getDocIdAsString(event)) || []
          if (ruleNames.length === 0) return '-'
          return ruleNames.join(', ')
        },
      })
    }

    return columns
  }, [workPeriodEvents, mutation, onClick, eventRulesMap])
}

export default WorkPeriodEventsList
